// use-create-team-from-session.ts — Teams suite, action
//
// Saves a session's team configuration as a re-usable template (= creates a new team
// from the session's current state).

'use client';

import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { TeamsListFilter } from '@cabal/shared/domain/session/team/enums.js';
import type {
  CreateTeamFromSessionRequest,
  TeamDetail,
  TeamPreviewListItem,
} from '@cabal/shared/domain/session/team/types.js';
import { useApi } from '@/contexts/api';
import { useActionHook } from '@/lib/query/use-action-hook';
import type { ActionResult } from '@/types/async-protocol';
import {
  teamsDetailKey,
  teamsPreviewKey,
  teamsRootKey,
} from './team-query-keys';

function isModelId(value: number | null): value is number {
  return value !== null;
}

function buildPreviewFromTeamDetail(team: TeamDetail): TeamPreviewListItem {
  return {
    id: team.id,
    name: team.name,
    catchPhrase: team.catchPhrase,
    category: team.category,
    tagIds: team.tagIds,
    isPublic: team.isPublic,
    isSaved: true,
    originatedFromTeam: team.originatedFromTeam,
    publicRootTeamId: team.publicRootTeamId,
    ownerId: team.ownerUserId,
    displayOrder: team.displayOrder,
    modelIds: team.members.map((member) => member.modelId).filter(isModelId),
  };
}

function upsertPreviewTeam(input: {
  teams: TeamPreviewListItem[] | undefined;
  team: TeamPreviewListItem;
}): TeamPreviewListItem[] {
  const teams = input.teams ?? [];
  const existingIndex = teams.findIndex((team) => team.id === input.team.id);

  if (existingIndex === -1) {
    return [...teams, input.team];
  }

  return teams.map((team) => (
    team.id === input.team.id ? input.team : team
  ));
}

export function useCreateTeamFromSession(): ActionResult<
  CreateTeamFromSessionRequest,
  TeamDetail
> {
  const api = useApi();
  const queryClient = useQueryClient();

  return useActionHook<CreateTeamFromSessionRequest, TeamDetail>(
    useMemo(
      () => ({
        mutationFn: async (request) => {
          const createdTeam = await api.teams.createFromSession({ body: request });
          return api.teams.byId(createdTeam.id).get();
        },
        onInvalidate: (team) => {
          const previewTeam = buildPreviewFromTeamDetail(team);
          queryClient.setQueryData<TeamDetail>(teamsDetailKey(team.id), team);
          // The preview cache is split by filter, so the write goes to the slice
          // the new team actually belongs to. Every team minted here is the
          // viewer's own, hence `owned`; the public slice is touched only in the
          // one case where the team is also in the catalogue.
          queryClient.setQueryData<TeamPreviewListItem[]>(
            teamsPreviewKey(TeamsListFilter.Owned),
            (teams) => upsertPreviewTeam({ teams, team: previewTeam }),
          );
          if (previewTeam.isPublic) {
            queryClient.setQueryData<TeamPreviewListItem[]>(
              teamsPreviewKey(TeamsListFilter.Public),
              (teams) => upsertPreviewTeam({ teams, team: previewTeam }),
            );
          }
          queryClient.invalidateQueries({ queryKey: teamsRootKey });
        },
      }),
      [api, queryClient],
    ),
  );
}
